import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

export type TitleType = 'beginner' | 'apprentice' | 'scholar' | 'expert' | 'master' | 'legend';

interface TitleBadgeProps {
  title: TitleType;
  size?: number;
  showLabel?: boolean;
  className?: string;
}

// Title configurations
const TITLE_CONFIG: Record<TitleType, {
  label: { en: string; 'zh-TW': string };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
  };
  icon: 'seed' | 'book' | 'scroll' | 'star' | 'crown' | 'gem';
}> = {
  beginner: {
    label: { en: 'Beginner', 'zh-TW': '初學者' },
    colors: {
      primary: '#4CAF50',
      secondary: '#81C784',
      accent: '#C8E6C9',
      text: '#1B5E20',
    },
    icon: 'seed',
  },
  apprentice: {
    label: { en: 'Apprentice', 'zh-TW': '學徒' },
    colors: {
      primary: '#CD7F32',
      secondary: '#DDA15E',
      accent: '#F4A460',
      text: '#5D4037',
    },
    icon: 'book',
  },
  scholar: {
    label: { en: 'Scholar', 'zh-TW': '學者' },
    colors: {
      primary: '#9E9E9E',
      secondary: '#BDBDBD',
      accent: '#E0E0E0',
      text: '#424242',
    },
    icon: 'scroll',
  },
  expert: {
    label: { en: 'Expert', 'zh-TW': '專家' },
    colors: {
      primary: '#FFD700',
      secondary: '#FFE55C',
      accent: '#FFFACD',
      text: '#8B6914',
    },
    icon: 'star',
  },
  master: {
    label: { en: 'Master', 'zh-TW': '大師' },
    colors: {
      primary: '#E5E4E2',
      secondary: '#F5F5F5',
      accent: '#FFFFFF',
      text: '#37474F',
    },
    icon: 'crown',
  },
  legend: {
    label: { en: 'Legend', 'zh-TW': '傳奇' },
    colors: {
      primary: '#B9F2FF',
      secondary: '#E0FFFF',
      accent: '#FFFFFF',
      text: '#006064',
    },
    icon: 'gem',
  },
};

// SVG Icon components
const Icons: Record<string, React.FC<{ color: string; size: number }>> = {
  seed: ({ color, size }) => (
    <g transform={`translate(${size * 0.3}, ${size * 0.25}) scale(${size / 100})`}>
      <ellipse cx="20" cy="30" rx="12" ry="18" fill={color} />
      <path d="M20 12 Q25 20 20 30 Q15 20 20 12" fill={color} opacity="0.7" />
      <ellipse cx="20" cy="30" rx="6" ry="10" fill="white" opacity="0.3" />
    </g>
  ),
  book: ({ color, size }) => (
    <g transform={`translate(${size * 0.25}, ${size * 0.25}) scale(${size / 100})`}>
      <rect x="5" y="10" width="40" height="30" rx="2" fill={color} />
      <rect x="8" y="13" width="34" height="24" fill="white" opacity="0.9" />
      <line x1="25" y1="13" x2="25" y2="37" stroke={color} strokeWidth="2" />
      <rect x="10" y="16" width="12" height="2" fill={color} opacity="0.5" />
      <rect x="10" y="21" width="10" height="2" fill={color} opacity="0.5" />
      <rect x="28" y="16" width="12" height="2" fill={color} opacity="0.5" />
      <rect x="28" y="21" width="10" height="2" fill={color} opacity="0.5" />
    </g>
  ),
  scroll: ({ color, size }) => (
    <g transform={`translate(${size * 0.25}, ${size * 0.2}) scale(${size / 100})`}>
      <rect x="10" y="8" width="30" height="35" rx="2" fill="white" opacity="0.9" />
      <rect x="10" y="8" width="30" height="35" rx="2" fill="none" stroke={color} strokeWidth="2" />
      <circle cx="10" cy="8" r="5" fill={color} />
      <circle cx="40" cy="8" r="5" fill={color} />
      <circle cx="10" cy="43" r="5" fill={color} />
      <circle cx="40" cy="43" r="5" fill={color} />
      <rect x="15" y="15" width="20" height="2" fill={color} opacity="0.6" />
      <rect x="15" y="21" width="18" height="2" fill={color} opacity="0.6" />
      <rect x="15" y="27" width="20" height="2" fill={color} opacity="0.6" />
      <rect x="15" y="33" width="15" height="2" fill={color} opacity="0.6" />
    </g>
  ),
  star: ({ color, size }) => (
    <g transform={`translate(${size * 0.25}, ${size * 0.2}) scale(${size / 100})`}>
      <polygon
        points="25,0 31,18 50,18 35,30 41,48 25,38 9,48 15,30 0,18 19,18"
        fill={color}
      />
      <polygon
        points="25,8 29,18 40,18 31,26 35,38 25,32 15,38 19,26 10,18 21,18"
        fill="white"
        opacity="0.4"
      />
    </g>
  ),
  crown: ({ color, size }) => (
    <g transform={`translate(${size * 0.22}, ${size * 0.25}) scale(${size / 100})`}>
      <path
        d="M5 35 L5 20 L15 28 L27.5 10 L40 28 L50 20 L50 35 Z"
        fill={color}
      />
      <rect x="5" y="35" width="45" height="8" rx="2" fill={color} />
      <circle cx="15" cy="18" r="4" fill={color} />
      <circle cx="27.5" cy="8" r="5" fill={color} />
      <circle cx="40" cy="18" r="4" fill={color} />
      <path
        d="M10 35 L10 24 L17 29 L27.5 16 L38 29 L45 24 L45 35 Z"
        fill="white"
        opacity="0.3"
      />
    </g>
  ),
  gem: ({ color, size }) => (
    <g transform={`translate(${size * 0.22}, ${size * 0.2}) scale(${size / 100})`}>
      <polygon points="27.5,0 50,18 40,50 15,50 5,18" fill={color} />
      <polygon points="27.5,5 45,18 27.5,45 10,18" fill="white" opacity="0.5" />
      <polygon points="27.5,10 38,18 27.5,38 17,18" fill={color} opacity="0.7" />
      <line x1="5" y1="18" x2="50" y2="18" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="27.5" y1="0" x2="27.5" y2="18" stroke="white" strokeWidth="1" opacity="0.6" />
      {/* Sparkle effects */}
      <circle cx="20" cy="12" r="2" fill="white">
        <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="35" cy="25" r="1.5" fill="white">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
      </circle>
    </g>
  ),
};

export const TitleBadge: React.FC<TitleBadgeProps> = ({
  title,
  size = 60,
  showLabel = false,
  className = '',
}) => {
  const { t } = useTranslation('progression');
  const uniqueId = useId();
  const config = TITLE_CONFIG[title];
  const IconComponent = Icons[config.icon];

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="drop-shadow-md"
      >
        <defs>
          {/* Badge gradient */}
          <linearGradient id={`badge-gradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.colors.accent} />
            <stop offset="50%" stopColor={config.colors.primary} />
            <stop offset="100%" stopColor={config.colors.secondary} />
          </linearGradient>

          {/* Glow effect for premium titles */}
          <filter id={`badge-glow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer glow for premium badges */}
        {(title === 'expert' || title === 'master' || title === 'legend') && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size / 2 - 4}
            fill="none"
            stroke={config.colors.primary}
            strokeWidth="3"
            opacity="0.4"
            filter={`url(#badge-glow-${uniqueId})`}
          />
        )}

        {/* Badge background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 2 - 4}
          fill={`url(#badge-gradient-${uniqueId})`}
        />

        {/* Inner highlight */}
        <circle
          cx={size / 2}
          cy={size / 2 - size * 0.1}
          r={size / 3}
          fill="white"
          opacity="0.2"
        />

        {/* Border */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 2 - 4}
          fill="none"
          stroke={config.colors.secondary}
          strokeWidth="2"
        />

        {/* Icon */}
        <IconComponent color={config.colors.text} size={size} />

        {/* Legend special effect - rotating particles */}
        {title === 'legend' && (
          <g>
            {[0, 60, 120, 180, 240, 300].map((angle, i) => (
              <circle
                key={angle}
                cx={size / 2 + Math.cos((angle * Math.PI) / 180) * (size / 2 - 8)}
                cy={size / 2 + Math.sin((angle * Math.PI) / 180) * (size / 2 - 8)}
                r="2"
                fill="white"
              >
                <animate
                  attributeName="opacity"
                  values="1;0.2;1"
                  dur={`${1 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </g>
        )}
      </svg>

      {showLabel && (
        <span
          className="mt-1 text-xs font-medium"
          style={{ color: config.colors.text }}
        >
          {t(`titles.${title}`)}
        </span>
      )}
    </div>
  );
};

export default TitleBadge;
